import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { useTenant } from '../context/TenantContext';

/**
 * GlobalBackground component displays a fixed background image across all pages
 * with configurable opacity. Settings are fetched from business settings.
 *
 * iter308w — Skip rendering entirely on:
 *  - The platform host (`my.vyomos.io`, etc — `branding.is_platform`).
 *    Platform pages are deliberately tenant-neutral; bleeding a tenant
 *    background image (e.g. DAS tiger) through the super-admin shell
 *    leaked the host-tenant default fallback into UX.
 *  - All `/super-admin/*` routes regardless of host — the super-admin
 *    UI must always look like the platform, not like whatever tenant
 *    happens to be resolved.
 *  - When the cached localStorage hostname doesn't match the current
 *    hostname (defensive — busts stale DAS tiger after cutover).
 */
const GlobalBackground = () => {
  const [backgroundUrl, setBackgroundUrl] = useState(null);
  const [opacity, setOpacity] = useState(0.1);
  const [loaded, setLoaded] = useState(false);
  const { branding } = useTenant();
  const location = useLocation();

  // iter308w — Skip-rendering predicates. Evaluated EVERY render so a
  // navigation from /admin/* into /super-admin/* immediately hides the
  // tenant background.
  const onSuperAdminRoute = typeof location?.pathname === 'string'
    && location.pathname.startsWith('/super-admin');
  // Hostname-based platform check — synchronous, so we skip the
  // tenant-scoped /admin/background-settings call on platform/marketing
  // hosts even before async `branding` resolves (avoids a cosmetic 400
  // from the anonymous fetch that has no tenant to resolve).
  const _platformHosts = new Set(
    (process.env.REACT_APP_PLATFORM_HOSTS || 'www.vyomos.io,vyomos.io,my.vyomos.io,localhost,127.0.0.1')
      .split(',').map((h) => h.trim().toLowerCase()).filter(Boolean)
  );
  const onPlatformHostByName = typeof window !== 'undefined'
    && _platformHosts.has((window.location.hostname || '').toLowerCase());
  const onPlatformHost = !!branding?.is_platform || onPlatformHostByName;

  useEffect(() => {
    // iter308w — Invalidate cross-host cached background URLs so a
    // visitor who saw the DAS tiger at the pre-cutover my.vyomos.io
    // doesn't see it forever after re-deploys.
    try {
      const cachedHost = localStorage.getItem('backgroundImageHost');
      const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
      if (cachedHost && cachedHost !== currentHost) {
        localStorage.removeItem('backgroundImageUrl');
        localStorage.removeItem('backgroundOpacity');
        localStorage.removeItem('backgroundImageHost');
      }
    } catch (_e) { /* ignore */ }

    // Skip the API call entirely on platform host / super-admin routes
    if (onPlatformHost || onSuperAdminRoute) {
      setBackgroundUrl(null);
      setLoaded(true);
      return;
    }

    const fetchBackgroundSettings = async () => {
      try {
        // Check localStorage first for faster initial render
        const cachedUrl = localStorage.getItem('backgroundImageUrl');
        const cachedOpacity = localStorage.getItem('backgroundOpacity');

        if (cachedUrl) {
          setBackgroundUrl(cachedUrl);
          setOpacity(parseFloat(cachedOpacity) || 0.1);
        }

        const response = await api.get('/admin/background-settings');
        const { background_image_url, background_opacity } = response.data;

        if (background_image_url) {
          setBackgroundUrl(background_image_url);
          localStorage.setItem('backgroundImageUrl', background_image_url);
          if (typeof window !== 'undefined') {
            localStorage.setItem('backgroundImageHost', window.location.hostname);
          }
        } else {
          setBackgroundUrl(null);
          localStorage.removeItem('backgroundImageUrl');
          localStorage.removeItem('backgroundImageHost');
        }

        if (background_opacity !== undefined) {
          setOpacity(background_opacity);
          localStorage.setItem('backgroundOpacity', background_opacity.toString());
        }

        setLoaded(true);
      } catch (error) {
        console.log('Background settings not available');
        setLoaded(true);
      }
    };

    fetchBackgroundSettings();
  }, [onPlatformHost, onSuperAdminRoute]);

  // iter308w — hide on platform/super-admin even if URL is still in
  // state from a previous render.
  if (onPlatformHost || onSuperAdminRoute) return null;
  if (!backgroundUrl || !loaded) return null;

  const fullUrl = backgroundUrl.startsWith('http')
    ? backgroundUrl
    : `${(typeof window !== "undefined" && window.location ? window.location.origin : process.env.REACT_APP_BACKEND_URL)}${backgroundUrl}`;

  return (
    <div
      className="fixed inset-0 pointer-events-none z-0"
      style={{
        backgroundImage: `url(${fullUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        opacity: opacity
      }}
      aria-hidden="true"
    />
  );
};

export default GlobalBackground;
